/**
 * Guest Checkout Page - Account creation at payment
 * Flow: Browse → Add to cart → Checkout → Create account → Pay
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Link2, 
  ArrowLeft,
  MapPin,
  CreditCard,
  CheckCircle,
  Tag,
  Loader2,
  Truck,
  Wallet,
  Banknote,
  Shield,
  Clock,
  Zap,
  Rocket,
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Phone,
  ShoppingBag,
  ArrowRight
} from 'lucide-react';

const GuestCheckout = () => {
  const navigate = useNavigate();
  const { cart, loading: cartLoading, isGuestCart, clearCart } = useCart();
  const { isAuthenticated, login, register } = useAuth();
  
  const [step, setStep] = useState(1); // 1: Cart Review, 2: Contact Info, 3: Address, 4: Account, 5: Payment
  const [processing, setProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState(null);
  
  // Contact Info (captured early)
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  
  // Address
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [parish, setParish] = useState('Kingston');
  
  // Account Creation (before payment)
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [createAccount, setCreateAccount] = useState(true);
  
  // Payment
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [shippingSpeed, setShippingSpeed] = useState('standard');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);

  // Redirect to regular checkout if already logged in
  useEffect(() => {
    if (isAuthenticated && !isGuestCart) {
      navigate('/checkout');
    }
  }, [isAuthenticated, isGuestCart, navigate]);

  // Redirect if cart is empty
  useEffect(() => {
    if (!cartLoading && cart.items.length === 0 && !orderComplete) {
      toast.error('Your cart is empty');
      navigate('/shop');
    }
  }, [cart, cartLoading, navigate, orderComplete]);

  const parishes = [
    'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
    'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
    'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
  ];

  const shippingOptions = [
    { id: 'standard', name: 'Standard', icon: Truck, desc: '3-5 days', price: 500, color: 'blue' },
    { id: 'express', name: 'Express', icon: Zap, desc: '1-2 days', price: 1200, color: 'amber' },
    { id: 'same_day', name: 'Same Day', icon: Rocket, desc: 'Today', price: 2000, color: 'emerald' }
  ];

  const paymentMethods = [
    { id: 'stripe', name: 'Card Payment', icon: CreditCard, desc: 'Visa, Mastercard' },
    { id: 'cod', name: 'Cash on Delivery', icon: Banknote, desc: 'Pay when delivered' }
  ];

  const getShippingPrice = () => {
    return shippingOptions.find(o => o.id === shippingSpeed)?.price || 500;
  };

  const calculateTotal = () => {
    return (cart.total || 0) + getShippingPrice();
  };

  // Validate email format
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Step 1: Review Cart - just displays items
  // Step 2: Contact Info - Name, Email, Phone
  const handleContactNext = async () => {
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!email.trim() || !isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Track abandoned cart (for recovery emails)
    try {
      await api.post('/abandoned-carts/track', {
        email: email.trim(),
        name: name.trim(),
        phone: phone || null,
        cart_items: cart.items,
        cart_total: cart.total,
        step_reached: 2
      });
    } catch (e) {
      // Silent fail - don't block checkout
      console.log('Cart tracking:', e);
    }
    
    setStep(3);
  };

  // Step 3: Address
  const handleAddressNext = async () => {
    if (!street.trim() || !city.trim()) {
      toast.error('Please enter your delivery address');
      return;
    }
    
    // Update abandoned cart tracking with progress
    try {
      await api.post('/abandoned-carts/track', {
        email: email.trim(),
        name: name.trim(),
        phone: phone || null,
        cart_items: cart.items,
        cart_total: cart.total,
        step_reached: 3
      });
    } catch (e) {
      console.log('Cart tracking:', e);
    }
    
    setStep(4);
  };

  // Step 4: Create Account (before payment)
  const handleAccountNext = async () => {
    if (createAccount) {
      if (!password || password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
      
      setProcessing(true);
      try {
        // Register new account
        await register({
          name,
          email,
          phone: phone || null,
          password
        });
        toast.success('Account created! Proceeding to payment...');
        setStep(5);
      } catch (error) {
        const msg = error.response?.data?.error?.message || 'Failed to create account';
        if (msg.includes('already exists') || msg.includes('already registered')) {
          toast.error('Email already registered. Please sign in.');
          // Offer to login
          setCreateAccount(false);
        } else {
          toast.error(msg);
        }
      } finally {
        setProcessing(false);
      }
    } else {
      // Guest checkout without account
      setStep(5);
    }
  };

  // Login existing user
  const handleLogin = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }
    setProcessing(true);
    try {
      await login(email, password);
      toast.success('Logged in! Proceeding to payment...');
      setStep(5);
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setProcessing(false);
    }
  };

  // Step 5: Complete Order
  const handlePlaceOrder = async () => {
    setProcessing(true);
    try {
      // Create order
      const orderData = {
        delivery_address: { street, city, parish },
        payment_provider: paymentMethod,
        shipping_speed: shippingSpeed,
        shipping_fee: getShippingPrice(),
        promo_code: promoCode || null,
        guest_info: !isAuthenticated ? { name, email, phone } : null
      };

      const response = await api.post('/orders/checkout', orderData);

      // For demo, auto-confirm payment (except COD)
      if (paymentMethod !== 'cod') {
        await api.post(`/orders/${response.data.order_id}/confirm-payment`, null, {
          params: { payment_reference: response.data.payment_client_secret || 'demo_payment' }
        });
      }

      setOrderId(response.data.order_id);
      setOrderComplete(true);
      clearCart();
      
      // Mark cart as converted (for abandoned cart tracking)
      try {
        await api.post(`/abandoned-carts/convert/${encodeURIComponent(email)}`);
      } catch (e) {
        console.log('Cart conversion tracking:', e);
      }
      
      toast.success('Order placed successfully!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Checkout failed';
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  // Loading state
  if (cartLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  // Order Complete
  if (orderComplete) {
    const shipping = shippingOptions.find(o => o.id === shippingSpeed);
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-emerald-600 text-white px-6 py-4">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <Link2 className="h-6 w-6" />
            <span className="font-bold text-lg">Order Confirmed</span>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 py-12 text-center">
          <div className="h-24 w-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Thank You, {name}!</h1>
          <p className="text-gray-600 mb-2">Your order has been placed successfully.</p>
          <p className="text-sm text-gray-500 font-mono mb-4">Order ID: {orderId?.slice(0, 8)}</p>
          <p className="text-sm text-gray-500 mb-6">
            Confirmation email sent to <span className="font-medium">{email}</span>
          </p>
          
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-${shipping?.color}-50 mb-8`}>
            {shipping && <shipping.icon className={`w-5 h-5 text-${shipping.color}-500`} />}
            <span className="font-medium">{shipping?.name} Delivery</span>
            <span className="text-gray-500">- {shipping?.desc}</span>
          </div>
          
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/orders')} variant="outline" className="border-emerald-500 text-emerald-600 rounded-xl">
              View Orders
            </Button>
            <Button onClick={() => navigate('/shop')} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl">
              Continue Shopping
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-emerald-600 text-white px-6 py-4 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Button variant="ghost" onClick={() => step > 1 ? setStep(step - 1) : navigate('/cart')} className="text-white hover:bg-emerald-700 p-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Link2 className="h-6 w-6" />
          <span className="font-bold text-lg">Checkout</span>
          <div className="ml-auto flex gap-1">
            {[1,2,3,4,5].map(s => (
              <div key={s} className={`w-2 h-2 rounded-full ${step >= s ? 'bg-white' : 'bg-white/30'}`} />
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-6 space-y-6">
        {/* Step 1: Cart Review */}
        {step === 1 && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Review Your Cart</h2>
            <Card className="divide-y divide-gray-100">
              {cart.items.map(item => (
                <div key={item.product_id} className="p-4 flex gap-4">
                  <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden">
                    {item.image ? (
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-800">{item.title}</h3>
                    <p className="text-sm text-gray-500">{item.business_name}</p>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-gray-500">Qty: {item.quantity}</span>
                      <span className="font-bold text-emerald-600">J${(item.price * item.quantity).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </Card>
            
            <div className="bg-white rounded-xl p-4 border">
              <div className="flex justify-between text-gray-600 mb-2">
                <span>Subtotal</span>
                <span>J${cart.total?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-gray-600 mb-2">
                <span>Shipping</span>
                <span className="text-sm text-gray-400">Calculated next</span>
              </div>
            </div>

            <Button onClick={() => setStep(2)} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl h-14">
              Continue to Checkout
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <p className="text-center text-sm text-gray-500">
              No account required until payment
            </p>
          </>
        )}

        {/* Step 2: Contact Info */}
        {step === 2 && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Contact Information</h2>
            <Card className="p-6 space-y-4">
              <div>
                <Label className="text-gray-700 flex items-center gap-2">
                  <User className="w-4 h-4" /> Full Name *
                </Label>
                <Input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="John Doe"
                  className="mt-1 h-12"
                  data-testid="checkout-name-input"
                />
              </div>
              <div>
                <Label className="text-gray-700 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email Address *
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1 h-12"
                  data-testid="checkout-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">Order confirmation will be sent here</p>
              </div>
              <div>
                <Label className="text-gray-700 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Phone (optional)
                </Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="876-XXX-XXXX"
                  className="mt-1 h-12"
                  data-testid="checkout-phone-input"
                />
              </div>
            </Card>

            <Button onClick={handleContactNext} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl h-14">
              Continue to Delivery
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </>
        )}

        {/* Step 3: Delivery Address */}
        {step === 3 && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Delivery Address</h2>
            <Card className="p-6 space-y-4">
              <div>
                <Label className="text-gray-700">Street Address *</Label>
                <Input
                  value={street}
                  onChange={e => setStreet(e.target.value)}
                  placeholder="123 Main Street"
                  className="mt-1 h-12"
                  data-testid="checkout-street-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">City *</Label>
                  <Input
                    value={city}
                    onChange={e => setCity(e.target.value)}
                    placeholder="Kingston"
                    className="mt-1 h-12"
                    data-testid="checkout-city-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Parish</Label>
                  <Select value={parish} onValueChange={setParish}>
                    <SelectTrigger className="mt-1 h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {parishes.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>

            {/* Shipping Speed */}
            <Card className="p-6">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5 text-emerald-500" /> Shipping Speed
              </h3>
              <div className="space-y-3">
                {shippingOptions.map(opt => {
                  const Icon = opt.icon;
                  const selected = shippingSpeed === opt.id;
                  return (
                    <div
                      key={opt.id}
                      onClick={() => setShippingSpeed(opt.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-4 ${
                        selected ? `border-${opt.color}-500 bg-${opt.color}-50` : 'border-gray-200'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selected ? `bg-${opt.color}-100` : 'bg-gray-100'}`}>
                        <Icon className={`w-5 h-5 ${selected ? `text-${opt.color}-500` : 'text-gray-400'}`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800">{opt.name}</p>
                        <p className="text-sm text-gray-500">{opt.desc}</p>
                      </div>
                      <p className="font-bold">J${opt.price.toLocaleString()}</p>
                      {selected && <CheckCircle className="w-5 h-5 text-emerald-500" />}
                    </div>
                  );
                })}
              </div>
            </Card>

            <Button onClick={handleAddressNext} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl h-14">
              Continue
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </>
        )}

        {/* Step 4: Account Creation */}
        {step === 4 && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Create Your Account</h2>
            <p className="text-gray-600 mb-4">
              Create an account to track your order and save your details for faster checkout next time.
            </p>
            
            <Card className="p-6 space-y-4">
              <div className="bg-emerald-50 p-4 rounded-lg">
                <p className="text-sm text-emerald-700">
                  <CheckCircle className="w-4 h-4 inline mr-2" />
                  Account will be created with: <strong>{email}</strong>
                </p>
              </div>

              {createAccount ? (
                <>
                  <div>
                    <Label className="text-gray-700 flex items-center gap-2">
                      <Lock className="w-4 h-4" /> Create Password *
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Min 8 characters"
                        className="h-12 pr-10"
                        data-testid="checkout-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Min 8 characters</p>
                  </div>

                  <Button 
                    onClick={handleAccountNext} 
                    disabled={processing}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl h-14"
                  >
                    {processing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    Create Account & Continue
                  </Button>

                  <button 
                    onClick={() => setCreateAccount(false)}
                    className="w-full text-sm text-gray-500 hover:text-gray-700"
                  >
                    Already have an account? Sign in instead
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Sign in to your existing account:
                  </p>
                  <div>
                    <Label className="text-gray-700">Password</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="h-12 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <Button 
                    onClick={handleLogin} 
                    disabled={processing}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl h-14"
                  >
                    {processing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    Sign In & Continue
                  </Button>

                  <button 
                    onClick={() => setCreateAccount(true)}
                    className="w-full text-sm text-gray-500 hover:text-gray-700"
                  >
                    Need to create a new account?
                  </button>
                </>
              )}
            </Card>
          </>
        )}

        {/* Step 5: Payment */}
        {step === 5 && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Payment</h2>

            {/* Payment Method */}
            <Card className="p-6">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-500" /> Payment Method
              </h3>
              <div className="space-y-3">
                {paymentMethods.map(method => {
                  const Icon = method.icon;
                  const selected = paymentMethod === method.id;
                  return (
                    <div
                      key={method.id}
                      onClick={() => setPaymentMethod(method.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-4 ${
                        selected ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selected ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                        <Icon className={`w-5 h-5 ${selected ? 'text-emerald-600' : 'text-gray-400'}`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800">{method.name}</p>
                        <p className="text-sm text-gray-500">{method.desc}</p>
                      </div>
                      {selected && <CheckCircle className="w-5 h-5 text-emerald-500" />}
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Promo Code */}
            <Card className="p-6">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Tag className="w-5 h-5 text-emerald-500" /> Promo Code
              </h3>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter code"
                  value={promoCode}
                  onChange={e => setPromoCode(e.target.value.toUpperCase())}
                  className="h-11"
                />
                <Button variant="outline" className="border-emerald-500 text-emerald-600">
                  Apply
                </Button>
              </div>
            </Card>

            {/* Order Summary */}
            <Card className="p-6">
              <h3 className="font-bold text-gray-800 mb-4">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal ({cart.items.length} items)</span>
                  <span>J${cart.total?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping ({shippingOptions.find(o => o.id === shippingSpeed)?.name})</span>
                  <span>J${getShippingPrice().toLocaleString()}</span>
                </div>
                <div className="border-t pt-2 mt-2 flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-emerald-600">J${calculateTotal().toLocaleString()}</span>
                </div>
              </div>
            </Card>

            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
              <Shield className="w-4 h-4" />
              <span>Secure checkout by Liantiao</span>
            </div>

            <Button
              onClick={handlePlaceOrder}
              disabled={processing}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl h-14 font-medium text-lg"
              data-testid="place-order-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : paymentMethod === 'cod' ? (
                'Place Order (Pay on Delivery)'
              ) : (
                `Pay J$${calculateTotal().toLocaleString()}`
              )}
            </Button>
          </>
        )}
      </main>
    </div>
  );
};

export default GuestCheckout;
